/**
 * ThemeManager class to handle theme switching and persistence.
 * Options:
 * - storageKeyTheme: localStorage key for storing the theme (default: "rt-theme")
 * - storageKeyInitialised: localStorage key for storing initialisation state (default: "rt-theme-initialised")
 * - themes: array of available theme names (default: ["default", "dark"])
 * - defaultTheme: default theme name (default: first in themes array)
 */

class ThemeManager {
    constructor(options) {
        options = options || {};
        this.storageKeyTheme = options.storageKeyTheme || "rt-theme";
        this.storageKeyInitialised = options.storageKeyInitialised || "rt-theme-initialised";
        this.themes = options.themes || ["default", "dark"];
        this.defaultTheme = options.defaultTheme || this.themes[0];
        this.currentTheme = this.defaultTheme;
        this.buttons = Array.prototype.slice.call(
            document.querySelectorAll(".rt-theme-switch")
        );
        this.handleToggleClick = this.handleToggleClick.bind(this);
        for (var i = 0; i < this.buttons.length; i++) {
            this.buttons[i].addEventListener("click", this.handleToggleClick);
        }
        this.initialiseTheme();
    }

    async initialiseTheme() {

        // 1 - use localStorage for initial render, or default
        var storedTheme = this.getStoredTheme();
        if (storedTheme) {
            this.applyTheme(storedTheme);
        } else {
            this.applyTheme(this.defaultTheme);
        }

        // 2 - try to get the current theme from server
        var serverTheme = await this.getThemeFromServer();
        if (serverTheme) {
            if (serverTheme !== this.currentTheme) {
                this.applyTheme(serverTheme);
            }
            this.setStoredTheme(serverTheme);
            this.setIsInitialised(true);
        } else {
            if (!storedTheme) {
                this.setStoredTheme(this.currentTheme);
            }
            this.setIsInitialised(true);
        }
    }

    getStoredTheme() {
        try {
            return window.localStorage.getItem(this.storageKeyTheme);
        } catch (e) {
            return null;
        }
    }

    setStoredTheme(themeName) {
        try {
            window.localStorage.setItem(this.storageKeyTheme, themeName);
        } catch (e) { }
    }

    getIsInitialised() {
        try {
            return window.localStorage.getItem(this.storageKeyInitialised) === "true";
        } catch (e) {
            return false;
        }
    }

    setIsInitialised(value) {
        try {
            window.localStorage.setItem(this.storageKeyInitialised, value ? "true" : "false");
        } catch (e) { }
    }

    async getThemeFromServer() {
        try {
            if (!window.rtAPI ||
                !rtAPI.accessibility ||
                !rtAPI.accessibility.call ||
                !rtAPI.accessibility.call.getCurrentThemeDataAttribute) {
                return null;
            }
            var response = await rtAPI.accessibility.call.getCurrentThemeDataAttribute();
            if (!response) {
                return null;
            }

            // only treat the server as authoritative if the user is logged in
            var isLoggedIn = false;
            if (typeof response.isLoggedIn === "boolean") {
                isLoggedIn = response.isLoggedIn;
            } else if (typeof response.loginStatus === "string") {
                isLoggedIn = response.loginStatus.toLowerCase() === "loggedin";
            }
            if (!isLoggedIn) {
                // if logged out then do not override localStorage with server value
                return null;
            }
            var themeName = null;
            if (typeof response.currentThemeDataAttribute === "string" &&
                response.currentThemeDataAttribute.length > 0) {
                themeName = response.currentThemeDataAttribute;

            } else if (response.header &&
                response.header.data &&
                typeof response.header.data.currentThemeDataAttribute === "string") {
                themeName = response.header.data.currentThemeDataAttribute;
            }
            if (themeName && themeName.length > 0) {
                return themeName;
            }
            return null;
        } catch (e) {
            return null;
        }
    }

    applyTheme(themeName) {
        this.currentTheme = themeName;

        var root = document.documentElement;
        root.setAttribute("data-theme", themeName);

        this.updateButtons();
    }

    updateButtons() {
        for (var i = 0; i < this.buttons.length; i++) {
            var button = this.buttons[i];

            button.setAttribute("data-current-theme", this.currentTheme);

            var labelAttr = "data-label-" + this.currentTheme;
            var label = button.getAttribute(labelAttr);

            if (label) {
                button.setAttribute("aria-label", label);
            }
        }
    }

    async handleToggleClick(event) {
        event.preventDefault();

        var idx = this.themes.indexOf(this.currentTheme);
        if (idx === -1) {
            idx = 0;
        }

        var nextTheme = this.themes[(idx + 1) % this.themes.length];

        await this.changeTheme(nextTheme);
    }

    async changeTheme(themeName) {
        this.applyTheme(themeName);
        this.setStoredTheme(themeName);
        this.setIsInitialised(true);

        try {
            if (window.rtAPI &&
                rtAPI.preferences &&
                rtAPI.preferences.call &&
                rtAPI.preferences.call.updatePreference) {
                await rtAPI.preferences.call.updatePreference("theme", themeName);
            }
        } catch (e) {
        }
    }
}

